/**
 * contactStage — which step of the sequence a contact is on, and which topic a
 * prepared draft belongs to.
 *
 * `lead_contacted` doubles as a pipeline flag: an outreach job writes
 * "In Outreach" there while the real step lives in `lead_status`. Reading
 * `lead_contacted` alone turned every job contact into a fresh lead, so a
 * follow-up composed for them was sent as an intro.
 */

import { isFollowUpEmailType } from '../../shared/enums';

const STEP_STATUSES = new Set([
  'Not Contacted',
  'New',
  'Import',
  'Introduced',
  'First Follow-up',
  '1. Follow-UP',
  'Second Follow-up',
  '2. Follow-UP',
  'Third Follow-up',
  '3. Follow-UP',
]);

const STATUS_BY_LAST_EMAIL_TYPE = {
  intro: 'Introduced',
  follow_up_1: 'First Follow-up',
  follow_up_2: 'Second Follow-up',
  follow_up_3: 'Third Follow-up',
};

/**
 * The first sequence-step status on the contact: `lead_contacted`, then
 * `lead_status`, then the legacy `status`, then the type of the last mail
 * sent. Falls back to the raw value so callers keep their own handling of
 * anything else.
 */
export function effectiveContactStatus(contact) {
  if (!contact) return '';
  const candidates = [contact.lead_contacted, contact.lead_status, contact.status];
  const step = candidates.find((s) => typeof s === 'string' && STEP_STATUSES.has(s));
  if (step) return step;
  const fromLastEmail = STATUS_BY_LAST_EMAIL_TYPE[contact.lead_last_email?.type];
  if (fromLastEmail) return fromLastEmail;
  return contact.lead_contacted || contact.lead_status || '';
}

/**
 * Topic name for a prepared draft: its own topic when the mapping knows it,
 * otherwise the first topic of the same email type.
 */
export function topicForPreparedDraft({ topicName, emailType }, topicTypeMapping = {}) {
  if (topicName && topicTypeMapping[topicName]) return topicName;
  if (emailType) {
    const byType = Object.keys(topicTypeMapping).find((t) => topicTypeMapping[t] === emailType);
    if (byType) return byType;
  }
  return topicName || null;
}

const REPLY_SUBJECT = /^\s*(re|aw|wg|fw|fwd)\s*:/i;

/**
 * Mirror of the server's `correctedEmailType`: an "intro" whose subject is the
 * contact's prepared follow-up subject (or a reply subject) is that follow-up.
 */
export function correctedDraftEmailType({ requested, prepared, subject }) {
  if (requested !== 'intro') return requested;
  if (!prepared || !isFollowUpEmailType(prepared.emailType)) return requested;
  const outgoing = String(subject || '').trim();
  const draft = String(prepared.subject || '').trim();
  if ((draft && outgoing === draft) || REPLY_SUBJECT.test(outgoing)) return prepared.emailType;
  return requested;
}
